import { getPreference, setPreference } from "../../../data/preferencesClient.js";

export const UPDATES_ORDER_KEY = "updates_custom_order";
export const ORDER_SAVE_DELAY = 350;
export const validCustomOrder = (value) => Array.isArray(value) && value.every((id) => typeof id === "string");

// Serialize even across reader unmount/remount. A new reader waits for a
// navigation flush before GET, so a slow old PUT cannot win over a new move.
const writes = new Map();
const editVersions = new Map();

export function createCustomOrderSession({ scope, allowed, onOrder, onError, onSaved,
  read = getPreference, write = setPreference }) {
  const currentVersion = () => editVersions.get(scope) || 0;
  let revision = currentVersion();
  let locallyEdited = false;
  let pending = null;
  let failed = null;
  let timer = null;
  let disposed = false;

  const flush = () => {
    clearTimeout(timer);
    timer = null;
    if (!pending || !allowed()) return writes.get(scope) || Promise.resolve();
    const ids = pending;
    const version = revision;
    pending = null;
    const previous = writes.get(scope);
    const run = async () => {
      if (!allowed()) return;
      try {
        await write(UPDATES_ORDER_KEY, ids, { keepalive: true });
        if (version === currentVersion()) {
          failed = null;
          onSaved?.();
        }
      } catch (error) {
        // An older failure must not mark a newer arrangement as failed.
        if (version === currentVersion() && allowed()) {
          failed = ids;
          onError(error, () => {
            if (!allowed() || version !== currentVersion()) return;
            pending = failed;
            void flush();
          });
        }
      }
    };
    // Start immediately when idle (important for pagehide), otherwise wait.
    const promise = previous ? previous.then(run) : run();
    writes.set(scope, promise);
    void promise.finally(() => { if (writes.get(scope) === promise) writes.delete(scope); });
    return promise;
  };

  return {
    async load() {
      // A Retry action from an old load error must not re-read over an
      // arrangement the user has since explicitly edited (even if unsaved).
      if (locallyEdited) return;
      const version = revision;
      try {
        await writes.get(scope);
        if (disposed || !allowed()) return;
        const value = await read(UPDATES_ORDER_KEY);
        if (!disposed && allowed() && version === currentVersion() && validCustomOrder(value)) onOrder(value);
      } catch (error) {
        if (!disposed && allowed() && version === currentVersion()) onError(error);
      }
    },
    reorder(ids) {
      if (disposed || !allowed()) return;
      locallyEdited = true;
      revision = currentVersion() + 1;
      editVersions.set(scope, revision);
      failed = null;
      pending = [...ids];
      onOrder(pending);
      clearTimeout(timer);
      timer = setTimeout(flush, ORDER_SAVE_DELAY);
    },
    flush,
    dispose() {
      disposed = true;
      clearTimeout(timer);
      // Ordinary navigation flushes; an identity transition discards unsent
      // work instead of sending it under the next account's credentials.
      return flush();
    },
  };
}
